import {
	AccessRule,
	Actions,
	LoginResult,
	ScopedAccessRule,
	ScopedPermissionKey,
	ScopeType,
	Subjects,
} from 'ihub/common';
import { Dispatch, SetStateAction } from 'react';
import { NavigateFunction } from 'react-router-dom';
import HttpServices from '../classes/http-services';
import SupportedLocales from '../components/custom-controls/local-selector/supported-locales-type';
import AvailableThemes from '../components/custom-controls/theme-selector/available-themes-type';
import { chatbotMsgHistoryKey } from '../components/main-app-bar/rag-chat-window/rag-chat-window';
import { localeKey, themeKey } from '../constants';
import AppRoutes from '../enums/app-routes';
import QueryClientWrapper from '../utils/create-query-client';

const userNameKey = 'UserName';
const userPermissionsKey = 'UserPermissions';
const userPasswordExpiredKey = 'UserPasswordExpired';
const accessTokenKey = 'JWT';
const tenantKey = 'CurrentTenant';
const qrCodeKey = 'signInQrCode';
const sessionTasks = 'copiedTasks';
const sessionTaskMappings = 'copiedMappings';
const scopedPermissionsKey = 'UserScopedPermissions';
const adminFlagKey = 'userIsAdmin';

export function GetSessionToken(): string {
	const authCookie = GetAccessToken();
	if (!authCookie) {
		throw new Error('No auth token present.');
	}
	return authCookie.replace('Bearer ', '').trim();
}

export function GetUserName(): string | undefined {
	return sessionStorage.getItem(userNameKey) ?? undefined;
}

export function GetUserPermissions(): AccessRule[] | undefined {
	const res = sessionStorage.getItem(userPermissionsKey);
	if (!res) {
		return undefined;
	}
	return JSON.parse(res);
}

export function GetUserScopedPermissions(): ScopedAccessRule[] | undefined {
	const res = sessionStorage.getItem(scopedPermissionsKey);
	if (!res) {
		return undefined;
	}
	return JSON.parse(res);
}

function IsUserAdmin(): boolean {
	const res = sessionStorage.getItem(adminFlagKey);
	if (!res) {
		return false;
	}
	return JSON.parse(res);
}

export function GetPasswordReset(): string | undefined {
	return sessionStorage.getItem(userPasswordExpiredKey) ?? undefined;
}

export function GetAccessToken(): string | undefined {
	return sessionStorage.getItem(accessTokenKey) ?? undefined;
}

export function GetTheme() {
	return (sessionStorage.getItem(themeKey) ?? 'defaultDarkTheme') as AvailableThemes;
}

export function GetLocale() {
	return (sessionStorage.getItem(localeKey) ?? 'enUS') as SupportedLocales;
}

export function GetTenantID() {
	return sessionStorage.getItem(tenantKey);
}

export function SetTenantID(id: string) {
	return sessionStorage.setItem(tenantKey, id);
}

export function ClearTenantID() {
	sessionStorage.removeItem(tenantKey);
}

export function GetQRCode() {
	return sessionStorage.getItem(qrCodeKey);
}

export function ClearQRCode() {
	sessionStorage.removeItem(qrCodeKey);
}

export function ClearUserPasswordExpired() {
	sessionStorage.removeItem(userPasswordExpiredKey);
}

export function userHasPermissions(subject: Subjects, action: Actions) {
	const userPermissions = GetUserPermissions();
	if (!userPermissions) return false;
	//Check if the user has admin privileges
	if (
		userPermissions.find(x => x.subject.toString() === 'all' && x.action.includes(Actions.manage))
	) {
		return true;
	}
	const userSubject = userPermissions.find(x => x.subject === subject);
	if (!userSubject) return false;
	if (userSubject.action.includes(Actions.manage)) return true;
	return userSubject.action.includes(action);
}

// temporary function to add the permissions to session when creating new projects/clients
// prevents having to logout
export function addScopedPermissionsToSession(scopeType: ScopeType, scopeID: string) {
	const trimmedScopeID = scopeID.trim();
	if (!trimmedScopeID) return;

	const scopedPermissions = GetUserScopedPermissions() ?? [];
	const permissionKeys =
		scopeType === ScopeType.Client
			? ['users', 'variables', 'secrets', 'dashboard', 'projectSpaceConfig', 'clientSpaceConfig']
			: ['users', 'variables', 'secrets', 'dashboard', 'projectSpaceConfig', 'integrationConfig'];

	for (const key of permissionKeys) {
		const subject = `${trimmedScopeID}_${key}`;
		const existingPermission = scopedPermissions.find(
			permission => permission.subject.toString() === subject
		);

		if (!existingPermission) {
			scopedPermissions.push({
				subject,
				action: [Actions.manage],
			});
		} else if (!existingPermission.action.includes(Actions.manage)) {
			existingPermission.action.push(Actions.manage);
		}
	}

	sessionStorage.setItem(scopedPermissionsKey, JSON.stringify(scopedPermissions));
}

/**
 * Determines whether the current user has the requested permission for a
 * specific client or project scope. The user must have the corresponding
 * global permission and either scoped manage permission or the requested
 * action. Scope-level manage permissions grant access to all subjects.
 */
export function userHasScopedPermissions(
	scopeID: string,
	scopeType: ScopeType,
	subject: ScopedPermissionKey,
	action: Actions
): boolean {
	if (IsUserAdmin()) return true;
	const hasUserPermission = userHasPermissions(
		scopeType === ScopeType.Client ? Subjects.ClientSpaceConfig : Subjects.ProjectSpaceConfig,
		action
	);
	if (!hasUserPermission) return false;
	const scopedPermissions = GetUserScopedPermissions();

	if (!scopedPermissions) return false;

	const scopedSubject = `${scopeID}_${subject}`;

	const hasScopedPermission = scopedPermissions.some(
		permission =>
			(permission.subject.toString() === `${scopeID}_all` &&
				permission.action.includes(Actions.manage)) ||
			(permission.subject === scopedSubject &&
				(permission.action.includes(Actions.manage) || permission.action.includes(action)))
	);

	if (hasUserPermission && !hasScopedPermission) return false;

	return true;
}

export function userHasFlowPermissions(
	action: Actions,
	clientSpaceConfigId?: string,
	projectSpaceConfigId?: string,
	scopedPermission?: { key: ScopedPermissionKey; action: Actions; scopeType: ScopeType }
): boolean {
	if (IsUserAdmin()) return true;
	const hasUserPermission = userHasPermissions(Subjects.FlowConfig, action);

	if (!hasUserPermission) return false;

	if (scopedPermission && clientSpaceConfigId && projectSpaceConfigId) {
		const hasScopedPermission = userHasScopedPermissions(
			scopedPermission.scopeType === ScopeType.Client ? clientSpaceConfigId : projectSpaceConfigId,
			scopedPermission.scopeType,
			scopedPermission.key,
			scopedPermission.action
		);

		if (!hasScopedPermission) return false;
	}
	return true;
}

export function ClearSessionUser() {
	sessionStorage.removeItem(userNameKey);
	sessionStorage.removeItem(userPermissionsKey);
	sessionStorage.removeItem(userPasswordExpiredKey);
	sessionStorage.removeItem(accessTokenKey);
	sessionStorage.removeItem(tenantKey);
	sessionStorage.removeItem(chatbotMsgHistoryKey);
	sessionStorage.removeItem(sessionTaskMappings);
	sessionStorage.removeItem(sessionTasks);
	sessionStorage.removeItem(scopedPermissionsKey);
	sessionStorage.removeItem(adminFlagKey);
}

export const signInSuccess = (
	loginResult: LoginResult,
	navigate: NavigateFunction,
	setTenant: Dispatch<SetStateAction<string | undefined>>
) => {
	QueryClientWrapper.queryClient.invalidateQueries();
	setTenant(loginResult.defaultTenantID);
	sessionStorage.setItem(userNameKey, loginResult.userName);
	sessionStorage.setItem(userPermissionsKey, JSON.stringify(loginResult.userPermissions));
	sessionStorage.setItem(scopedPermissionsKey, JSON.stringify(loginResult.scopedPermissions));
	sessionStorage.setItem(tenantKey, loginResult.defaultTenantID);
	sessionStorage.setItem(accessTokenKey, `Bearer ${loginResult.accessToken}`);
	sessionStorage.setItem(adminFlagKey, JSON.stringify(loginResult.isAdmin));
	if (loginResult.resetPassword) {
		sessionStorage.setItem(userPasswordExpiredKey, '1');
	}
	if (!loginResult.mfaEnabled) {
		HttpServices.auth.enableMFA({
			success: response => {
				if (!response) return;
				sessionStorage.setItem(qrCodeKey, response.dataURL);
				navigate(AppRoutes.MfaScreen());
			},
		});
	} else {
		navigate(AppRoutes.MfaScreen());
	}
};
